import * as React from 'react';
import { useRouter } from 'next/router';
import { appendQueryString } from 'lib/util/appendQueryString';
import { useMapUrl } from 'hooks/useMapUrl';
import { DigitalMap, DigitalMapPinStructuredInfoItem } from 'models/digitalMap';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { selectDigitalMap } from 'ducks/universal/digitalMap';
import { DEBUG } from 'lib/util/debug';

export type Page =
  | 'MAP'
  | 'LIST'
  | 'SCHEDULE'
  | 'TICKETS'
  | 'ACCOUNT'
  | 'NOTIFICATIONS'
  | 'ORDERS'
  | 'ITINERARIES.LIST'
  | 'ITINERARIES.CREATE'
  | 'ITINERARIES.EDIT'
  | 'ITINERARIES.EDIT_DETAILS'
  | 'ITINERARIES.DETAILS';

export type AttributeFilterType = 'hasTickets' | 'hasMobileOrders' | 'starred';

type QueryParameter = 'pin' | 'pind' | 'filters' | 'pg' | 'itin' | 'nav';

const getAllQueryParameters = (): QueryParameter[] => [
  'pin',
  'pind',
  'filters',
  'pg',
  'itin',
  'nav',
];

export interface Filters {
  tags: string[];
  categories: string[];
  attributes: AttributeFilterType[];

  structuredInfoItems: DigitalMapPinStructuredInfoItem[];

  showTimeFrom: string;
  showTimeTo: string;

  openAt: string;

  openNow: boolean;
  noWait: boolean;

  busRouteId: string | null;
}

export const getDefaultFilters = (): Filters => ({
  tags: [],
  categories: [],
  attributes: [],
  structuredInfoItems: [],
  showTimeFrom: '',
  showTimeTo: '',
  openAt: '',
  openNow: false,
  noWait: false,
  busRouteId: null,
});

export const getInitialFilters = (digitalMap: DigitalMap | null): Filters => {
  const initialSelectedCategories = digitalMap?.pin_categories
    .filter((category) => category.initially_selected)
    .map((category) => category.label);

  return {
    ...getDefaultFilters(),
    ...((initialSelectedCategories || []).length > 0 && {
      categories: initialSelectedCategories,
    }),
  };
};

export interface UrlQueryDisplayState {
  activePinKey: string;
  pinInfoDisplayStatus: 'SUMMARY' | 'DETAILS' | 'HIDDEN';
  setPinInfoDisplayState: (state: {
    pinKey: string | null;
    displayState: 'SUMMARY' | 'DETAILS' | 'HIDDEN';
  }) => void;

  filters: Filters;
  setFilters: (filters: Filters) => void;

  activePage: Page;
  setActivePage: (page: Page) => void;

  selectedItineraryId: string;
  replaceUrlParams: (updates: { [key in QueryParameter]?: string }) => void;

  pushUrlParams: (updates: { [key in QueryParameter]?: string }) => void;
  popUrlParams: () => void;
}

export const useUrlQueryDisplayState = (): UrlQueryDisplayState => {
  const router = useRouter();
  const mapUrl = useMapUrl();
  const digitalMap = useSelector(selectDigitalMap);

  const setPinInfoDisplayState = React.useCallback(
    async (state: { pinKey: string | null; displayState: 'SUMMARY' | 'DETAILS' | 'HIDDEN' }) => {
      const queryParams = new URLSearchParams();

      getAllQueryParameters().forEach((queryParam) => {
        if (router.query[queryParam]) {
          queryParams.set(queryParam, router.query[queryParam] as string);
        }
      });

      if (
        queryParams.get('pind') === state.displayState &&
        queryParams.get('pin') !== state.pinKey
      ) {
        return;
      }

      if (state.pinKey) {
        queryParams.set('pin', state.pinKey);
        queryParams.set('pind', state.displayState);
      } else {
        queryParams.delete('pin');
        queryParams.delete('pind');
      }

      DEBUG.log(
        '[useUrlQueryDisplayState] setPinInfoDisplayState',
        appendQueryString(mapUrl, queryParams.toString())
      );

      await router.replace(appendQueryString(mapUrl, queryParams.toString()), undefined, {
        shallow: true,
        scroll: false,
      });
    },
    [router, mapUrl]
  );

  const setFilters = React.useCallback(
    async (filters: Filters) => {
      const queryParams = new URLSearchParams();

      getAllQueryParameters().forEach((queryParam) => {
        if (router.query[queryParam]) {
          queryParams.set(queryParam, router.query[queryParam] as string);
        }
      });

      if (!_.isEqual(filters, getInitialFilters(digitalMap))) {
        const filterString = JSON.stringify(filters);
        queryParams.set('filters', filterString);
      } else {
        queryParams.delete('filters');
      }

      const newUrl = appendQueryString(mapUrl, queryParams.toString());

      DEBUG.log('[useUrlQueryDisplayState] setFilters', newUrl);

      await router.push(newUrl, undefined, {
        shallow: true,
        scroll: false,
      });
    },
    [router, mapUrl, digitalMap] // Added digitalMap to dependencies
  );

  const activePage = (router.query.pg as Page) || 'MAP';
  const setActivePage = React.useCallback(
    async (page: Page) => {
      const queryParams = new URLSearchParams();

      getAllQueryParameters().forEach((queryParam) => {
        if (router.query[queryParam]) {
          queryParams.set(queryParam, router.query[queryParam] as string);
        }
      });

      queryParams.set('pg', page);
      queryParams.delete('pind');
      queryParams.delete('pin');
      queryParams.delete('filters');
      if (page !== 'ITINERARIES.DETAILS' && page !== 'ITINERARIES.EDIT') {
        queryParams.delete('itin');
      }

      const newUrl = appendQueryString(mapUrl, queryParams.toString());

      DEBUG.log('[useUrlQueryDisplayState] setActivePage', newUrl);

      await router.push(newUrl, undefined, {
        shallow: true,
        scroll: false,
      });
    },
    [router, mapUrl]
  );

  // Update URL params for multiple query parameters at once to avoid race conditions

  const updateUrlParams = React.useCallback(
    async (updates: { [key in QueryParameter]?: string }, push: boolean) => {
      const queryParams = new URLSearchParams();

      getAllQueryParameters().forEach((queryParam) => {
        if (router.query[queryParam]) {
          queryParams.set(queryParam, router.query[queryParam] as string);
        }
      });

      if (updates.pg !== 'ITINERARIES.DETAILS' && updates.pg !== 'ITINERARIES.EDIT') {
        queryParams.delete('itin');
      }

      // Apply all updates at once
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          queryParams.set(key, value);
        } else {
          queryParams.delete(key);
        }
      });

      const newUrl = appendQueryString(mapUrl, queryParams.toString());
      if (push) {
        DEBUG.log('[useUrlQueryDisplayState] pushUrlParams', newUrl);
        await router.push(newUrl, undefined, {
          shallow: true,
          scroll: false,
        });
      } else {
        DEBUG.log('[useUrlQueryDisplayState] replaceUrlParams', newUrl);
        await router.replace(newUrl, undefined, {
          shallow: true,
          scroll: false,
        });
      }
    },
    [router, mapUrl]
  );

  const replaceUrlParams = React.useCallback(
    async (updates: { [key in QueryParameter]?: string }) => {
      return updateUrlParams(updates, false);
    },
    [updateUrlParams]
  );

  const pushUrlParams = React.useCallback(
    async (updates: { [key in QueryParameter]?: string }) => {
      return updateUrlParams(updates, true);
    },
    [updateUrlParams]
  );

  const popUrlParams = React.useCallback(async () => {
    await router.back();
  }, [router]);

  const selectedItineraryId = router.query.itin as string;

  const activePinKey = router.query.pin ? (router.query.pin as string) : '';
  const pinInfoDisplayStatus = router.query.pin
    ? (router.query.pind as 'SUMMARY' | 'DETAILS' | 'HIDDEN') || 'DETAILS'
    : 'HIDDEN';

  let filters = getInitialFilters(digitalMap);

  try {
    if (router.query.filters) {
      filters = JSON.parse(router.query.filters as string);
    }
  } catch (e) {
    console.error('Error parsing filters', e);

    filters = getInitialFilters(digitalMap);
  }

  return {
    activePinKey,
    pinInfoDisplayStatus,
    setPinInfoDisplayState,
    filters,
    setFilters,
    activePage,
    setActivePage,
    selectedItineraryId,
    replaceUrlParams,
    pushUrlParams,
    popUrlParams,
  };
};
